/* ============================================================
   Админка · Пользователи — список всех аккаунтов и их данных
   (логин/никнейм, почта, телефон, команда, тип, даты).
   Пароли хранятся необратимым хешем и НЕ показываются — вместо
   этого есть сброс пароля для восстановления доступа.
   ============================================================ */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppStore } from '../../store';
import { Ic } from '../icons';
import { AdmCard, EmptyRow } from './ui';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('ru-RU'); } catch (e) { return String(iso).slice(0, 10); }
};

const TYPE_LABEL = { admin: 'Админ', captain: 'Капитан', user: 'Игрок' };

function TypePill({ type }) {
  const isAdmin = type === 'admin';
  const isCap = type === 'captain';
  const color = isAdmin ? 'var(--fed-accent-bright)' : isCap ? 'var(--fed-gold)' : 'var(--fed-fg-muted)';
  const bg = isAdmin ? 'rgba(225,18,23,.12)' : isCap ? 'rgba(230,183,60,.12)' : 'var(--fed-surface-3)';
  return (
    <span className="inline-flex items-center rounded-full font-mono uppercase" style={{ padding: '3px 9px', fontSize: 10, letterSpacing: '.08em', color, background: bg }}>
      {TYPE_LABEL[type] || type || '—'}
    </span>
  );
}

// Копирование в буфер с фолбэком для незащищённого контекста
async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* фолбэк ниже */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

/* ===== Модалка сброса пароля ===== */
function ResetPasswordModal({ user, onClose }) {
  const resetUserPassword = useAppStore((s) => s.resetUserPassword);
  const fire = useAppStore((s) => s.fire);
  const [mode, setMode] = useState('auto');      // auto | manual
  const [manual, setManual] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);     // { password, generated } — после успеха
  const [copied, setCopied] = useState(false);

  const doReset = async () => {
    if (busy) return;
    if (mode === 'manual' && manual.trim().length < 8) {
      fire && fire('Пароль слишком короткий (минимум 8 символов)', 'error');
      return;
    }
    setBusy(true);
    try {
      const res = await resetUserPassword(user.id, mode === 'manual' ? manual.trim() : null);
      setResult(res);
    } catch (e) { /* тост уже показан в сторе */ }
    setBusy(false);
  };

  const doCopy = async () => {
    const ok = await copyText(result.password);
    setCopied(ok);
    fire && fire(ok ? 'Пароль скопирован' : 'Не удалось скопировать', ok ? 'ok' : 'error');
  };

  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center"
      style={{ background: 'rgba(4,4,6,.72)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', padding: 16, paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}
      onClick={onClose}
    >
      <div
        className="fed-admin-modal-card w-full rounded-2xl border border-line"
        style={{ maxWidth: 460, maxHeight: '88vh', overflowY: 'auto', background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))', boxShadow: '0 30px 80px -20px rgba(0,0,0,.7)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* шапка */}
        <div className="flex items-center gap-3 border-b border-line" style={{ padding: '16px 18px' }}>
          <span className="flex items-center justify-center rounded-xl flex-shrink-0" style={{ width: 40, height: 40, background: 'rgba(225,18,23,.1)', color: 'var(--fed-accent-bright)' }}>
            <Ic name="lock" size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold uppercase text-fg" style={{ fontSize: 15 }}>Сброс пароля</div>
            <div className="font-mono text-fg-faint truncate" style={{ fontSize: 11, marginTop: 2 }}>{user.nickname || user.email || `#${user.id}`}</div>
          </div>
          <button className="fed-iconbtn" onClick={onClose} title="Закрыть"><Ic name="x" size={16} /></button>
        </div>

        <div style={{ padding: 18 }}>
          {!result ? (
            <>
              <div className="flex gap-2" style={{ marginBottom: 16 }}>
                <button
                  onClick={() => setMode('auto')}
                  className="flex-1 rounded-[10px] cursor-pointer font-display font-semibold uppercase border"
                  style={{ padding: '10px 8px', fontSize: 12, letterSpacing: '.03em', color: mode === 'auto' ? 'var(--fed-fg)' : 'var(--fed-fg-muted)', borderColor: mode === 'auto' ? 'var(--fed-line-accent)' : 'var(--fed-line)', background: mode === 'auto' ? 'rgba(225,18,23,.08)' : 'transparent' }}
                >
                  Сгенерировать
                </button>
                <button
                  onClick={() => setMode('manual')}
                  className="flex-1 rounded-[10px] cursor-pointer font-display font-semibold uppercase border"
                  style={{ padding: '10px 8px', fontSize: 12, letterSpacing: '.03em', color: mode === 'manual' ? 'var(--fed-fg)' : 'var(--fed-fg-muted)', borderColor: mode === 'manual' ? 'var(--fed-line-accent)' : 'var(--fed-line)', background: mode === 'manual' ? 'rgba(225,18,23,.08)' : 'transparent' }}
                >
                  Задать вручную
                </button>
              </div>

              {mode === 'manual' && (
                <label className="flex flex-col gap-[7px]" style={{ marginBottom: 16 }}>
                  <span className="fed-label">Новый пароль</span>
                  <input
                    className="fed-input"
                    type="text"
                    value={manual}
                    autoFocus
                    placeholder="Минимум 8 символов"
                    onChange={(e) => setManual(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') doReset(); }}
                  />
                </label>
              )}

              <p className="text-fg-muted" style={{ fontSize: 12.5, lineHeight: 1.6, marginBottom: 18 }}>
                {mode === 'auto'
                  ? 'Будет создан новый случайный пароль. Он появится здесь один раз — скопируйте и передайте пользователю.'
                  : 'Пользователь сможет войти по никнейму или почте с этим паролем. Передайте его владельцу аккаунта.'}
              </p>

              <div className="flex justify-end gap-2.5">
                <button onClick={onClose} className="fed-btn-ghost h-11 px-4 rounded-[10px]" style={{ fontSize: 13 }}>Отмена</button>
                <button
                  onClick={doReset}
                  disabled={busy}
                  className="h-11 px-5 border-none rounded-[10px] cursor-pointer font-display font-bold uppercase text-white inline-flex items-center gap-2"
                  style={{ fontSize: 13, letterSpacing: '.04em', opacity: busy ? 0.6 : 1, background: 'linear-gradient(120deg,var(--fed-accent-bright),var(--fed-accent-deep))', boxShadow: '0 10px 26px -12px var(--fed-accent-glow)' }}
                >
                  <Ic name="lock" size={15} />
                  {busy ? 'Сбрасываем…' : 'Сбросить пароль'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-fg-soft" style={{ fontSize: 13, marginBottom: 12 }}>
                <span style={{ color: 'var(--fed-ok)' }}><Ic name="check" size={16} /></span>
                Новый пароль установлен
              </div>

              <div className="rounded-xl border border-line-2 flex items-center gap-3" style={{ padding: '14px 16px', background: 'var(--fed-surface-3)' }}>
                <code className="flex-1 font-mono text-fg" style={{ fontSize: 18, letterSpacing: '.04em', wordBreak: 'break-all' }}>{result.password}</code>
                <button className="fed-iconbtn flex-shrink-0" onClick={doCopy} title="Скопировать">
                  <Ic name={copied ? 'check' : 'doc'} size={16} />
                </button>
              </div>

              <p style={{ fontSize: 12.5, lineHeight: 1.6, marginTop: 14, color: 'var(--fed-gold)' }}>
                Пароль показывается только сейчас и не хранится в открытом виде. Скопируйте его и передайте пользователю — после закрытия окна восстановить его будет нельзя (только сбросить заново).
              </p>

              <p style={{ fontSize: 12.5, lineHeight: 1.6, marginTop: 10, color: 'var(--fed-fg-muted)' }}>
                Не сбрасывайте повторно, пока пользователь не попробовал войти: новый сброс сразу отменяет этот пароль, и человек будет вводить уже недействительный.
              </p>

              <div className="flex justify-end" style={{ marginTop: 18 }}>
                <button
                  onClick={onClose}
                  className="h-11 px-5 border-none rounded-[10px] cursor-pointer font-display font-bold uppercase text-white inline-flex items-center gap-2"
                  style={{ fontSize: 13, letterSpacing: '.04em', background: 'linear-gradient(120deg,var(--fed-accent-bright),var(--fed-accent-deep))' }}
                >
                  Готово
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===== Смена роли: игрок ⇄ капитан =====
   Права админа из панели не выдаются намеренно: это делается один раз
   на сервере (flask --app app create-admin), чтобы случайный клик не
   создал второго администратора. */
function RoleBtn({ user, onDone }) {
  const setUserRole = useAppStore((s) => s.setUserRole);
  const [busy, setBusy] = useState(false);
  const isCaptain = user.user_type === 'captain';

  const toggle = async () => {
    if (busy) return;
    const next = isCaptain ? 'user' : 'captain';
    if (!window.confirm(isCaptain
      ? `Снять роль капитана с ${user.nickname || user.email}?`
      : `Выдать роль капитана ${user.nickname || user.email}?`)) return;
    setBusy(true);
    try {
      onDone(await setUserRole(user.id, next));
    } catch (e) { /* тост уже показан в сторе */ }
    setBusy(false);
  };

  return (
    <button
      onClick={toggle}
      disabled={busy}
      className="inline-flex items-center gap-1.5 rounded-[9px] cursor-pointer border font-display font-semibold uppercase"
      style={{
        padding: '7px 11px', fontSize: 11, letterSpacing: '.03em', whiteSpace: 'nowrap',
        opacity: busy ? 0.6 : 1,
        color: isCaptain ? 'var(--fed-gold)' : 'var(--fed-fg-muted)',
        borderColor: isCaptain ? 'rgba(230,183,60,.4)' : 'var(--fed-line)',
        background: isCaptain ? 'rgba(230,183,60,.08)' : 'transparent',
      }}
      title={isCaptain ? 'Снять роль капитана' : 'Выдать роль капитана'}
    >
      <Ic name="medal" size={13} />
      {isCaptain ? 'Снять капитана' : 'Капитан'}
    </button>
  );
}

/* ===== Модалка блокировки / разблокировки ===== */
function BanModal({ user, onDone, onClose }) {
  const setUserActive = useAppStore((s) => s.setUserActive);
  const [busy, setBusy] = useState(false);
  const banning = user.is_active !== false;   // сейчас активен → действие «заблокировать»

  const apply = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const updated = await setUserActive(user.id, !banning);
      onDone(updated);
    } catch (e) { /* тост уже показан в сторе */ }
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center"
      style={{ background: 'rgba(4,4,6,.72)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', padding: 16, paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}
      onClick={onClose}
    >
      <div
        className="fed-admin-modal-card w-full rounded-2xl border border-line"
        style={{ maxWidth: 440, maxHeight: '88vh', overflowY: 'auto', background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))', boxShadow: '0 30px 80px -20px rgba(0,0,0,.7)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-line" style={{ padding: '16px 18px' }}>
          <span className="flex items-center justify-center rounded-xl flex-shrink-0" style={{ width: 40, height: 40, background: banning ? 'rgba(225,18,23,.12)' : 'rgba(67,217,106,.12)', color: banning ? 'var(--fed-accent-bright)' : 'var(--fed-ok)' }}>
            <Ic name={banning ? 'ban' : 'check'} size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold uppercase text-fg" style={{ fontSize: 15 }}>{banning ? 'Блокировка аккаунта' : 'Разблокировка'}</div>
            <div className="font-mono text-fg-faint truncate" style={{ fontSize: 11, marginTop: 2 }}>{user.nickname || user.email || `#${user.id}`}</div>
          </div>
          <button className="fed-iconbtn" onClick={onClose} title="Закрыть"><Ic name="x" size={16} /></button>
        </div>

        <div style={{ padding: 18 }}>
          <p className="text-fg-muted" style={{ fontSize: 13, lineHeight: 1.65, marginBottom: 18 }}>
            {banning
              ? 'Аккаунт будет немедленно отключён: вход, обновление токена и любые запросы к серверу перестанут работать. Используйте это, если пользователь угрожает работоспособности проекта. Разблокировать можно в любой момент.'
              : 'Доступ к аккаунту будет восстановлен — пользователь снова сможет входить и пользоваться сайтом.'}
          </p>

          <div className="flex justify-end gap-2.5">
            <button onClick={onClose} className="fed-btn-ghost h-11 px-4 rounded-[10px]" style={{ fontSize: 13 }}>Отмена</button>
            <button
              onClick={apply}
              disabled={busy}
              className="h-11 px-5 border-none rounded-[10px] cursor-pointer font-display font-bold uppercase text-white inline-flex items-center gap-2"
              style={{
                fontSize: 13, letterSpacing: '.04em', opacity: busy ? 0.6 : 1,
                background: banning ? 'linear-gradient(120deg,var(--fed-accent-bright),var(--fed-accent-deep))' : 'linear-gradient(120deg,#2e9e57,#1c7a40)',
                boxShadow: banning ? '0 10px 26px -12px var(--fed-accent-glow)' : 'none',
              }}
            >
              <Ic name={banning ? 'ban' : 'check'} size={15} />
              {busy ? 'Применяем…' : banning ? 'Заблокировать' : 'Разблокировать'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdmUsers() {
  const fetchUsers = useAppStore((s) => s.fetchUsers);
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState(null);
  const [banTarget, setBanTarget] = useState(null);
  const firstLoad = useRef(true);

  const load = async (query = '') => {
    setLoading(true);
    const list = await fetchUsers(query);
    setUsers(Array.isArray(list) ? list : []);
    setLoading(false);
  };

  // первичная загрузка + дебаунс поиска
  useEffect(() => {
    const t = setTimeout(() => { load(q.trim()); firstLoad.current = false; }, firstLoad.current ? 0 : 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => ({
    total: users.length,
    admins: users.filter((u) => u.user_type === 'admin').length,
    banned: users.filter((u) => u.is_active === false).length,
  }), [users]);

  // обновить одного пользователя в списке после бана/разбана без перезагрузки
  const patchUser = (updated) => {
    if (!updated) return;
    setUsers((list) => list.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
  };

  return (
    <div className="fed-admin-view flex flex-col gap-4">
      {/* поиск + счётчик */}
      <div className="flex items-center gap-2.5 flex-wrap">
        <div className="relative flex-1" style={{ minWidth: 240, maxWidth: 420 }}>
          <span className="absolute" style={{ left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--fed-fg-faint)' }}><Ic name="user" size={16} /></span>
          <input
            className="fed-input w-full"
            style={{ height: 42, paddingLeft: 38 }}
            placeholder="Поиск: никнейм, почта, имя…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <span className="font-mono text-fg-faint" style={{ fontSize: 11 }}>
          {stats.total} польз. · {stats.admins} админ.
          {stats.banned > 0 && <span style={{ color: 'var(--fed-accent-bright)' }}> · {stats.banned} заблок.</span>}
        </span>
      </div>

      <AdmCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="fed-tbl">
            <thead>
              <tr>
                <th>Пользователь</th>
                <th>Почта</th>
                <th>Телефон</th>
                <th>Команда</th>
                <th>Тип</th>
                <th>Регистрация</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const noPassword = u.has_password === false;
                const banned = u.is_active === false;
                const isAdmin = u.user_type === 'admin';
                return (
                  <tr key={u.id} style={banned ? { opacity: 0.55 } : undefined}>
                    <td data-wide>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center rounded-lg font-mono font-bold text-fg bg-surface-3 flex-shrink-0" style={{ width: 32, height: 32, fontSize: 13 }}>
                          {(u.nickname || u.full_name || u.email || '?')[0].toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <div className="font-semibold text-fg flex items-center gap-2" style={{ fontSize: 14 }}>
                            <span className="truncate">{u.nickname || '—'}</span>
                            {u.oauth_provider && (
                              <span className="font-mono uppercase rounded" style={{ fontSize: 8.5, padding: '1px 5px', letterSpacing: '.08em', color: 'var(--fed-info)', background: 'rgba(59,165,217,.14)' }}>{u.oauth_provider}</span>
                            )}
                            {banned && (
                              <span className="inline-flex items-center gap-1 font-mono uppercase rounded" style={{ fontSize: 8.5, padding: '1px 6px', letterSpacing: '.08em', color: 'var(--fed-accent-bright)', background: 'rgba(225,18,23,.14)' }}>
                                <Ic name="ban" size={9} /> Заблокирован
                              </span>
                            )}
                          </div>
                          {u.full_name ? <div className="font-mono text-fg-faint truncate" style={{ fontSize: 10.5, marginTop: 2 }}>{u.full_name}</div> : null}
                        </div>
                      </div>
                    </td>
                    <td data-label="Почта" className="font-mono" style={{ fontSize: 12.5, wordBreak: 'break-all' }}>{u.email || '—'}</td>
                    <td data-label="Телефон" className="font-mono" style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{u.phone || '—'}</td>
                    <td data-label="Команда" style={{ maxWidth: 160 }}>{u.desired_team || '—'}</td>
                    <td data-label="Тип"><TypePill type={u.user_type} /></td>
                    <td data-label="Регистрация" className="font-mono" style={{ whiteSpace: 'nowrap' }}>{fmtDate(u.created_at)}</td>
                    <td data-wide onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => setResetTarget(u)}
                          className="inline-flex items-center gap-1.5 rounded-[9px] cursor-pointer border font-display font-semibold uppercase"
                          style={{ padding: '7px 11px', fontSize: 11, letterSpacing: '.03em', color: 'var(--fed-accent-bright)', borderColor: 'var(--fed-line-accent)', background: 'rgba(225,18,23,.06)', whiteSpace: 'nowrap' }}
                          title={noPassword ? 'У аккаунта нет пароля (вход через OAuth) — задать пароль' : 'Сбросить пароль'}
                        >
                          <Ic name="lock" size={13} />
                          {noPassword ? 'Задать пароль' : 'Сбросить'}
                        </button>
                        {!isAdmin && <RoleBtn user={u} onDone={patchUser} />}
                        {!isAdmin && (
                          <button
                            onClick={() => setBanTarget(u)}
                            className="inline-flex items-center gap-1.5 rounded-[9px] cursor-pointer border font-display font-semibold uppercase"
                            style={banned
                              ? { padding: '7px 11px', fontSize: 11, letterSpacing: '.03em', color: 'var(--fed-ok)', borderColor: 'rgba(67,217,106,.4)', background: 'rgba(67,217,106,.08)', whiteSpace: 'nowrap' }
                              : { padding: '7px 11px', fontSize: 11, letterSpacing: '.03em', color: 'var(--fed-fg-muted)', borderColor: 'var(--fed-line)', background: 'transparent', whiteSpace: 'nowrap' }}
                            title={banned ? 'Разблокировать аккаунт' : 'Заблокировать аккаунт'}
                          >
                            <Ic name={banned ? 'check' : 'ban'} size={13} />
                            {banned ? 'Разблокировать' : 'Заблокировать'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!users.length && (
                <EmptyRow colSpan={7}>
                  {loading ? 'Загрузка…' : q.trim() ? 'Ничего не найдено' : 'Пользователей нет (или бэкенд недоступен)'}
                </EmptyRow>
              )}
            </tbody>
          </table>
        </div>
      </AdmCard>

      {resetTarget && <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} />}
      {banTarget && (
        <BanModal
          user={banTarget}
          onClose={() => setBanTarget(null)}
          onDone={(updated) => { patchUser(updated); setBanTarget(null); }}
        />
      )}
    </div>
  );
}
